import { For, type JSX, Show } from 'solid-js';
import { t, tPlural } from '../i18n';
import type { UtilBox } from '../lib/fleetUtilization';
import { decimal } from '../lib/time';

// "Automatic clean-up" (#6563, prototype `panel()` clean-up box): the counts the machine's
// last boot-time sweep reported (`stats.hygiene`, COUNT-ONLY by design: the finding list
// lives in the machine's own log, so there is no file list here), "N items could not be
// removed" when it recorded errors, and the reasons it left items alone on purpose.
// A machine that never reported a sweep says so, never "every file was removed".

const GB = 1e9;

export function MachineCleanup(props: { readonly box: UtilBox }): JSX.Element {
  const hygiene = () => props.box.stats?.hygiene ?? null;
  const failing = (): boolean => (hygiene()?.errors ?? 0) > 0;
  return (
    <section class="machine-panel__box" aria-labelledby="machine-panel-cleanup">
      <div class="machine-panel__box-head">
        <h3 id="machine-panel-cleanup" class="machine-panel__h3">
          {t('fleet.panel.cleanup')}
        </h3>
        <Show when={failing()}>
          <span class="fleet-verdict fleet-verdict--warn">{t('fleet.panel.cleanup_failing')}</span>
        </Show>
      </div>
      <Show
        when={hygiene()}
        fallback={<p class="machine-panel__muted">{t('fleet.panel.cleanup_none')}</p>}
      >
        {(h) => (
          <>
            <p>
              {t('fleet.panel.cleanup_ran', {
                findings: h().findings,
                actions: h().actions,
                freed: decimal(h().bytesFreed / GB),
              })}
              <Show when={h().errors > 0}>
                {' '}
                {tPlural('fleet.panel.cleanup_errors', h().errors, { count: h().errors })}
              </Show>
            </p>
            <Show when={(h().refused ?? 0) > 0}>
              <p class="machine-panel__muted">
                {tPlural('fleet.panel.cleanup_refused', h().refused ?? 0, {
                  count: h().refused ?? 0,
                })}
              </p>
              <ul class="machine-panel__reasons">
                <For each={h().refusedReasons ?? []}>{(reason) => <li>{reason}</li>}</For>
              </ul>
            </Show>
          </>
        )}
      </Show>
    </section>
  );
}
